import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Search, Truck } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { mensajeDe } from './mensajes';
import type {
  CatalogosDeProveedor,
  ConsultaDeProveedor,
  Proveedor,
  Regimen,
  TipoDeIdentificacion,
} from './contrato';

interface Formulario {
  tipoIdentificacion: TipoDeIdentificacion;
  identificacion: string;
  razonSocial: string;
  nombreComercial: string;
  direccion: string;
  telefono: string;
  correo: string;
  regimen: Regimen;
  obligadoContabilidad: boolean;
  parteRelacionada: boolean;
  actividadEconomica: string;
  estadoContribuyente: string;
  observaciones: string;
}

function vacio(): Formulario {
  return {
    tipoIdentificacion: '01',
    identificacion: '',
    razonSocial: '',
    nombreComercial: '',
    direccion: '',
    telefono: '',
    correo: '',
    regimen: 'RG',
    obligadoContabilidad: false,
    parteRelacionada: false,
    actividadEconomica: '',
    estadoContribuyente: '',
    observaciones: '',
  };
}

function desde(p: Proveedor): Formulario {
  return {
    tipoIdentificacion: p.tipoIdentificacion,
    identificacion: p.identificacion,
    razonSocial: p.razonSocial,
    nombreComercial: p.nombreComercial ?? '',
    direccion: p.direccion ?? '',
    telefono: p.telefono ?? '',
    correo: p.correo ?? '',
    regimen: p.regimen,
    obligadoContabilidad: p.obligadoContabilidad,
    parteRelacionada: p.parteRelacionada,
    actividadEconomica: p.actividadEconomica ?? '',
    estadoContribuyente: p.estadoContribuyente ?? '',
    observaciones: p.observaciones ?? '',
  };
}

export function PanelDelProveedor({
  proveedor,
  catalogos,
  alCerrar,
  alGuardar,
}: {
  proveedor: Proveedor | null;
  catalogos: CatalogosDeProveedor | undefined;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [datos, setDatos] = useState<Formulario>(proveedor ? desde(proveedor) : vacio());
  const [tomadoDelSri, setTomadoDelSri] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editando = proveedor !== null;

  function cambiar<C extends keyof Formulario>(campo: C, valor: Formulario[C]) {
    setDatos((previo) => ({ ...previo, [campo]: valor }));
  }

  const consultar = useMutation({
    mutationFn: () =>
      api.get<ConsultaDeProveedor>(
        `/proveedores/consulta/${encodeURIComponent(datos.identificacion.trim())}`,
      ),
    onSuccess: (r) => {
      if (r.yaRegistrado) {
        setError(
          `YA ESTÁ DADO DE ALTA: ${r.yaRegistrado.razonSocial}. BÚSQUELO EN LA LISTA EN VEZ DE CREARLO OTRA VEZ.`,
        );
        return;
      }
      if (!r.segunElEstado) {
        setError('NO CONSTA EN EL REGISTRO: ESCRIBA LOS DATOS A MANO.');
        return;
      }
      setError(null);
      setTomadoDelSri(true);
      setDatos((previo) => ({
        ...previo,
        razonSocial: r.segunElEstado?.nombre ?? previo.razonSocial,
        nombreComercial: r.segunElEstado?.nombreComercial ?? previo.nombreComercial,
        direccion: r.segunElEstado?.direccion ?? previo.direccion,
        telefono: r.segunElEstado?.telefono ?? previo.telefono,
        correo: r.segunElEstado?.correo ?? previo.correo,
        estadoContribuyente: r.segunElEstado?.estado ?? previo.estadoContribuyente,
      }));
      notificar.exito('Datos traídos del registro');
    },
    onError: (causa) =>
      setError(causa instanceof ErrorDeApi ? causa.message : 'No se pudo consultar'),
  });

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = { ...datos, tomadoDelSri, activo: proveedor?.activo ?? true };
      return editando
        ? api.put<Proveedor>(`/proveedores/${proveedor.id}`, cuerpo)
        : api.post<Proveedor>('/proveedores', cuerpo);
    },
    onSuccess: () => {
      notificar.exito(editando ? 'Proveedor actualizado' : 'Proveedor dado de alta');
      alGuardar();
    },
    onError: (causa) => {
      const mensaje = mensajeDe(causa, 'No se pudo guardar');
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const falta =
    datos.identificacion.trim() === ''
      ? 'Falta la identificación'
      : datos.razonSocial.trim() === ''
        ? 'Falta la razón social'
        : null;

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={Truck}
      titulo={editando ? 'Editar proveedor' : 'Nuevo proveedor'}
      descripcion="El régimen y la contabilidad deciden qué porcentajes se le retienen"
      alEnviar={(evento) => {
        evento.preventDefault();
        if (falta === null) {
          guardar.mutate();
        }
      }}
      enviando={guardar.isPending}
    >
      <div className="space-y-5">
        <DosColumnas>
          <CampoDelPanel etiqueta="Tipo" requerido>
            <select
              value={datos.tipoIdentificacion}
              onChange={(e) => cambiar('tipoIdentificacion', e.target.value as TipoDeIdentificacion)}
              disabled={editando}
              className={claseEntradaPanel}
            >
              {(catalogos?.tiposDeIdentificacion ?? []).map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.etiqueta.toLocaleUpperCase('es')}
                </option>
              ))}
            </select>
          </CampoDelPanel>

          <CampoDelPanel
            etiqueta="Identificación"
            requerido
            ayuda={editando ? 'No se cambia: sería otro proveedor' : undefined}
          >
            <span className="flex gap-2">
              <input
                value={datos.identificacion}
                onChange={(e) => cambiar('identificacion', e.target.value)}
                maxLength={20}
                disabled={editando}
                className={claseEntradaPanel}
              />
              {!editando && (
                <button
                  type="button"
                  onClick={() => consultar.mutate()}
                  disabled={datos.identificacion.trim() === '' || consultar.isPending}
                  title="Preguntar al registro quién es"
                  className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-armazon-300 px-3 text-[11px] font-bold uppercase text-armazon-700 transition-colors hover:bg-armazon-50 disabled:opacity-40"
                >
                  {consultar.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Search className="size-4" aria-hidden />
                  )}
                  Consultar
                </button>
              )}
            </span>
          </CampoDelPanel>
        </DosColumnas>

        <CampoDelPanel etiqueta="Razón social" requerido>
          <input
            value={datos.razonSocial}
            onChange={(e) => cambiar('razonSocial', e.target.value)}
            maxLength={255}
            className={`${claseEntradaPanel} uppercase`}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Nombre comercial">
          <input
            value={datos.nombreComercial}
            onChange={(e) => cambiar('nombreComercial', e.target.value)}
            maxLength={255}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <DosColumnas>
          <CampoDelPanel
            etiqueta="Régimen"
            requerido
            ayuda="Al negocio popular no se le retiene renta"
          >
            <select
              value={datos.regimen}
              onChange={(e) => cambiar('regimen', e.target.value as Regimen)}
              className={claseEntradaPanel}
            >
              {(catalogos?.regimenes ?? []).map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.etiqueta.toLocaleUpperCase('es')}
                </option>
              ))}
            </select>
          </CampoDelPanel>

          <CampoDelPanel etiqueta="Estado en el SRI" ayuda="Lo trae la consulta">
            <input
              value={datos.estadoContribuyente}
              onChange={(e) => cambiar('estadoContribuyente', e.target.value)}
              maxLength={30}
              className={claseEntradaPanel}
            />
          </CampoDelPanel>
        </DosColumnas>

        <div className="grid gap-2 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
          <label className="flex items-center gap-2 text-xs font-semibold uppercase text-armazon-700">
            <input
              type="checkbox"
              checked={datos.obligadoContabilidad}
              onChange={(e) => cambiar('obligadoContabilidad', e.target.checked)}
              className="size-4 rounded border-armazon-300"
            />
            Obligado a llevar contabilidad
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold uppercase text-armazon-700">
            <input
              type="checkbox"
              checked={datos.parteRelacionada}
              onChange={(e) => cambiar('parteRelacionada', e.target.checked)}
              className="size-4 rounded border-armazon-300"
            />
            Parte relacionada
          </label>
        </div>

        <DosColumnas>
          <CampoDelPanel etiqueta="Teléfono">
            <input
              value={datos.telefono}
              onChange={(e) => cambiar('telefono', e.target.value)}
              maxLength={50}
              className={claseEntradaPanel}
            />
          </CampoDelPanel>
          <CampoDelPanel etiqueta="Correo">
            <input
              type="email"
              value={datos.correo}
              onChange={(e) => cambiar('correo', e.target.value)}
              maxLength={120}
              className={claseEntradaPanel}
            />
          </CampoDelPanel>
        </DosColumnas>

        <CampoDelPanel etiqueta="Dirección">
          <input
            value={datos.direccion}
            onChange={(e) => cambiar('direccion', e.target.value)}
            maxLength={255}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Actividad económica">
          <input
            value={datos.actividadEconomica}
            onChange={(e) => cambiar('actividadEconomica', e.target.value)}
            maxLength={255}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Observaciones">
          <textarea
            value={datos.observaciones}
            onChange={(e) => cambiar('observaciones', e.target.value)}
            maxLength={1000}
            rows={2}
            className={`${claseEntradaPanel} h-auto py-2`}
          />
        </CampoDelPanel>

        {(error ?? falta) && (
          <p
            role={error ? 'alert' : undefined}
            className={`flex gap-2 text-[11px] font-semibold uppercase ${
              error ? 'text-red-700' : 'text-armazon-500'
            }`}
          >
            {error && <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />}
            <span>{error ?? falta}</span>
          </p>
        )}
      </div>
    </PanelLateral>
  );
}
